import React from 'react'

/**
 * Parses a text string and generates a tree structure for markdown-like formatting.
 *
 * @param {string} text - The input text to parse.
 * @returns {Array<{ type: string, text: string }>} Parsed tree structure.
 */
const parseTextToTree = text => {
  const regex = /_(.*?)_|\*\*(.*?)\*\*|~(.*?)~/g
  const result = []
  let lastIndex = 0

  text.replace(regex, (match, italicText, boldText, underlineText, offset) => {
    // Add text before the match as 'others'
    if (offset > lastIndex) {
      result.push({ type: 'others', text: text.slice(lastIndex, offset) })
    }

    // Add matched text with its type
    if (boldText) {
      result.push({ type: 'bold', text: boldText.trim() })
    } else if (italicText) {
      result.push({ type: 'italic', text: italicText.trim() })
    } else if (underlineText) {
      result.push({ type: 'underline', text: underlineText.trim() })
    }

    lastIndex = offset + match.length
  })

  // Add remaining text as 'others'
  if (lastIndex < text.length) {
    result.push({ type: 'others', text: text.slice(lastIndex) })
  }

  return result
}

/**
 * Renders the parsed tree structure into React components.
 *
 * @param {Array<{ type: string, text: string }>} tree - Parsed tree structure.
 * @returns {React.ReactNode} Rendered React components.
 */
const renderTree = tree => {
  return tree.map(item => {
    const key = `${item.type}-${item.text}`
    switch (item.type) {
      case 'bold':
        return <strong key={key}>{item.text}</strong>
      case 'italic':
        return <em key={key}>{item.text}</em>
      case 'underline':
        return <u key={key}>{item.text}</u>
      case 'others':
        return <span key={key}>{item.text}</span>
      default:
        return null
    }
  })
}

/**
 * ReactMarkdownV2 component to render markdown-like text with **bold**, _italic_, and ~underline~ support.
 *
 * @param {React.ReactNode} children - The input text to render.
 * @returns {React.ReactNode} Rendered markdown-like text.
 */
const ReactMarkdownV2 = ({ children }) => {
  const parsedTree = parseTextToTree(children)
  return <>{renderTree(parsedTree)}</>
}

export default ReactMarkdownV2
